import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import type { WheelWedge } from '@goodtime/shared';
import { Easings, TweenGroup } from '../../core/Tween';
import {
  formatStakeRow,
  type CentralLedgerLineItem,
} from './WheelResolveCentralLedgerLogic';

export {
  buildLedgerLineItems,
  formatStakeRow,
  formatWedge,
  projectPodiumRank,
  reconcileLedgerSubtotal,
  renderServerLedgerLines,
  WEDGE_BAKED_ITEMS,
  type CentralLedgerLineItem,
} from './WheelResolveCentralLedgerLogic';

/**
 * Personal end-of-spin ledger that sits in the wheel's centre once
 * the disc lands. Replaces the per-seat OutcomeChip grid — the user
 * cares about their OWN breakdown (stake → wedge → items → podium →
 * winnings) far more than rival chips. Producer feedback 2026-05-27:
 * "the user doesn't care about the other players, they'd rather see
 * a more detailed breakdown of what happened to them."
 *
 * <p>Layout (top → bottom):
 * <pre>
 *   🪙 50 × ×2                         100
 *   Bea's Bomb                          −40
 *   Hit Bea                             +10
 *   Podium (1st)                         ×2
 *   ─────────────────────────────────────
 *   WINNINGS                            140
 * </pre>
 *
 * <p>The host scene constructs once, then calls
 * {@link reveal()} with a {@link CentralLedgerData} payload once the
 * spin lands. {@link hide()} fades it out at next match start.
 */

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';

const PODIUM_MULTIPLIER_PERCENT = [100, 50, 20] as const;
const PODIUM_RANK_LABEL = ['1st', '2nd', '3rd'] as const;

export interface WheelResolveCentralLedgerOptions {
  /** Width of the panel in canvas pixels. */
  width: number;
  /** Optional coin glyph rendered before the stake line. */
  coinTexture?: Texture | null;
}

export interface CentralLedgerData {
  /** Pre-spin stake the player put down. */
  stake: number;
  /** Wedge the player landed on (after wedge-shift items). */
  wedge: WheelWedge;
  /** Stake-row value: the player's BASE spin result, reconciled so
   *  that {@code subtotal + Σ(lineItems deltas)} equals their true
   *  (non-negative) take. Computed via
   *  {@code reconcileLedgerSubtotal(payout, serverLines)} — NOT the
   *  raw final payout, which would double-count the item lines. */
  subtotal: number;
  /** Action cards affecting the local player (filtered by host). */
  lineItems: ReadonlyArray<CentralLedgerLineItem>;
  /** Player's rank (1-indexed) for podium tier — null if not in
   *  top 3. Computed from projected tokens at this point in the
   *  match. */
  podiumRank: 1 | 2 | 3 | null;
}

export class WheelResolveCentralLedger extends Container {
  private readonly bg: Graphics;
  private readonly rowsHost: Container;
  private readonly tweens = new TweenGroup();
  private readonly opts: WheelResolveCentralLedgerOptions;
  private readonly coinTexture: Texture | null;
  private revealed = false;

  constructor(options: WheelResolveCentralLedgerOptions) {
    super();
    this.opts = options;
    this.coinTexture = options.coinTexture ?? null;
    this.visible = false;
    this.alpha = 0;

    this.bg = new Graphics();
    this.addChild(this.bg);

    this.rowsHost = new Container();
    this.addChild(this.rowsHost);
  }

  /** Replace the layout + show. */
  reveal(data: CentralLedgerData): void {
    this.tweens.cancelAll();
    this.rowsHost.removeChildren();

    const rows: LedgerRow[] = [];
    rows.push({
      kind: 'stake',
      label: formatStakeRow(data.stake, data.subtotal, data.wedge),
      delta: data.subtotal,
    });
    for (const li of data.lineItems) {
      rows.push({ kind: 'item', label: li.label, delta: li.delta, blocked: li.blocked });
    }
    let runningTotal = data.subtotal;
    for (const li of data.lineItems) runningTotal += li.delta;
    let winnings = runningTotal;
    if (data.podiumRank !== null) {
      const percent = PODIUM_MULTIPLIER_PERCENT[data.podiumRank - 1] ?? 0;
      const rankLabel = PODIUM_RANK_LABEL[data.podiumRank - 1] ?? '';
      rows.push({
        kind: 'podium',
        label: `Podium (${rankLabel})`,
        deltaText: `×${(1 + percent / 100).toFixed(percent % 100 === 0 ? 0 : 1)}`,
      });
      winnings += Math.round((runningTotal * percent) / 100);
    }
    rows.push({ kind: 'total', label: 'WINNINGS', delta: winnings });

    this.layoutRows(rows);
    this.visible = true;
    this.revealed = true;
    this.tweens.start({
      durationMs: 360,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.alpha = p;
        this.scale.set(0.86 + p * 0.14);
      },
    });
  }

  hide(): void {
    if (!this.revealed) {
      this.visible = false;
      this.alpha = 0;
      return;
    }
    this.tweens.cancelAll();
    const start = this.alpha;
    this.tweens.start({
      durationMs: 220,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        this.alpha = start * (1 - p);
      },
      onComplete: () => {
        this.visible = false;
        this.alpha = 0;
        this.revealed = false;
      },
    });
  }

  destroyDisplay(): void {
    this.tweens.cancelAll();
    this.removeChildren();
  }

  private layoutRows(rows: ReadonlyArray<LedgerRow>): void {
    // Producer feedback 2026-05-27 — bg must grow with row count.
    // Two-pass layout: 1) measure exact content height, 2) lay
    // rows out top-down starting at -contentH/2 so the panel
    // centre stays at (0, 0). Previously a single-pass cursor
    // with a guessed start undershot tall ledgers by ~150px.
    const padX = 16;
    const padY = 14;
    const rowGap = 4;
    const rowFontSize = 14;
    const totalFontSize = 18;
    const dividerH = 1;
    const dividerGap = 6;
    const rowH = rowFontSize + 8;
    const totalRowH = totalFontSize + 12;
    const innerW = this.opts.width - padX * 2;

    // Pass 1: total content height (sum of row heights + gaps +
    // divider spacing above the total row).
    let contentH = 0;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      const isTotal = row.kind === 'total';
      const h = isTotal ? totalRowH : rowH;
      contentH += h;
      if (i < rows.length - 1) contentH += rowGap;
      if (isTotal && i > 0) {
        // Divider + gap above total.
        contentH += dividerH + dividerGap * 2;
      }
    }

    // Pass 2: render. Start cursorY at the top of the centred
    // content area; each row's centre sits at cursorY + h/2.
    let cursorY = -contentH / 2;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      const isTotal = row.kind === 'total';
      const h = isTotal ? totalRowH : rowH;

      if (isTotal && i > 0) {
        // Divider drawn at the START of the total row's slot.
        cursorY += dividerGap;
        const divY = cursorY + dividerH / 2;
        const divider = new Graphics();
        divider
          .moveTo(-innerW / 2, divY)
          .lineTo(innerW / 2, divY)
          .stroke({ color: 0xffd86b, alpha: 0.55, width: dividerH });
        this.rowsHost.addChild(divider);
        cursorY += dividerH + dividerGap;
      }

      const rowCentreY = cursorY + h / 2;
      const fontSize = isTotal ? totalFontSize : rowFontSize;

      // Left column: label (+ optional coin glyph for the stake row).
      const labelText = new Text({
        text: row.label,
        style: {
          fontFamily: FONT_FAMILY,
          fontSize,
          fontWeight: isTotal ? '900' : '700',
          fill: 0xffffff,
          letterSpacing: isTotal ? 1 : 0,
        },
      });
      labelText.anchor.set(0, 0.5);
      labelText.x = -innerW / 2;
      labelText.y = rowCentreY;

      // Coin glyph prefix for the stake row.
      if (row.kind === 'stake' && this.coinTexture) {
        const coin = new Sprite(this.coinTexture);
        coin.anchor.set(0, 0.5);
        const coinSize = Math.round(fontSize * 1.15);
        coin.width = coinSize;
        coin.height = coinSize;
        coin.x = -innerW / 2;
        coin.y = rowCentreY;
        this.rowsHost.addChild(coin);
        labelText.x = -innerW / 2 + coinSize + 6;
      }
      this.rowsHost.addChild(labelText);

      // Right column: delta or text override.
      const deltaText =
        row.deltaText !== undefined
          ? row.deltaText
          : row.delta !== undefined
            ? formatDelta(row.delta, row.kind)
            : '';
      const deltaColour = colourForRow(row);
      const deltaLabel = new Text({
        text: deltaText,
        style: {
          fontFamily: FONT_FAMILY,
          fontSize,
          fontWeight: '900',
          fill: deltaColour,
          letterSpacing: 0,
        },
      });
      deltaLabel.anchor.set(1, 0.5);
      deltaLabel.x = innerW / 2;
      deltaLabel.y = rowCentreY;
      if (row.blocked) deltaLabel.alpha = 0.55;
      this.rowsHost.addChild(deltaLabel);

      cursorY += h;
      if (i < rows.length - 1) cursorY += rowGap;
    }

    // Background sized to measured content + padding on top/bottom.
    const h = contentH + padY * 2;
    this.bg.clear();
    this.bg
      .roundRect(-this.opts.width / 2, -h / 2, this.opts.width, h, 14)
      .fill({ color: 0x0a0518, alpha: 0.92 })
      .roundRect(-this.opts.width / 2, -h / 2, this.opts.width, h, 14)
      .stroke({ color: 0xffd86b, alpha: 0.8, width: 2 });
  }
}

interface LedgerRow {
  kind: 'stake' | 'item' | 'podium' | 'total';
  label: string;
  delta?: number;
  deltaText?: string;
  blocked?: boolean;
}

function formatDelta(d: number, kind: LedgerRow['kind']): string {
  if (kind === 'total') return String(d);
  if (kind === 'stake') return String(d);
  return d > 0 ? `+${d}` : `${d}`;
}

function colourForRow(row: LedgerRow): number {
  if (row.kind === 'total') return 0xffd86b;
  if (row.kind === 'stake') return 0x9de54e;
  if (row.kind === 'podium') return 0xffd86b;
  if (row.delta === undefined) return 0xffffff;
  return row.delta >= 0 ? 0x9de54e : 0xff8e9d;
}

